import React, { useState, useEffect, useCallback } from 'react';
import { t } from '@lingui/macro';
import moment from 'moment';
import Loader from '../Loader';
import { IMart } from '../../../interfaces/types';
import { fetchFootfallTotal } from '../../../../reducers/api/getReportData';
import {
    getMondayOfWeek,
    getComparableMonday,
    getAttendanceZone,
    IReportComparisonRow,
    IReportBreakdownRow,
} from '../../../helpers/reportHelpers';
import { numberWithSpaces } from '../../../helpers/index';
import ReportComparisonChart from './ReportComparisonChart';
import ReportComparisonTable from './ReportComparisonTable';
import ReportGroupedBarChart from './ReportGroupedBarChart';
import ExportButtons from './ExportButtons';

interface IProps {
    mart: IMart;
    referenceDate: moment.Moment;
    yearsBack: number;
}

const ReportWeekly = ({ mart, referenceDate, yearsBack }: IProps) => {
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState<IReportComparisonRow[]>([]);
    const [locationRows, setLocationRows] = useState<IReportBreakdownRow[]>([]);
    const [zoneRows, setZoneRows] = useState<IReportBreakdownRow[]>([]);
    const [dailyRows, setDailyRows] = useState<IReportBreakdownRow[]>([]);
    const [yearColumns, setYearColumns] = useState<{ year: number; label: string }[]>([]);

    const monday = getMondayOfWeek(referenceDate);
    const mondayStr = monday.format('YYYY-MM-DD');
    const weekNum = monday.isoWeek();

    const fetchData = useCallback(async () => {
        setLoading(true);

        const attendanceZone = getAttendanceZone(mart);
        const zoneIds = attendanceZone ? [attendanceZone.id] : [];

        const cols: { year: number; label: string }[] = [];
        for (let y = 0; y <= yearsBack; y++) {
            const wMon = getComparableMonday(monday, y);
            const wSun = wMon.clone().add(6, 'days');
            cols.push({
                year: wMon.year(),
                label: wMon.format('DD.MM') + '–' + wSun.format('DD.MM.YYYY')
            });
        }
        setYearColumns(cols);

        try {
            // --- Section 1: Total weekly footfall comparison ---
            const totalPromises: Promise<number>[] = [];
            for (let y = 0; y <= yearsBack; y++) {
                const wMon = getComparableMonday(monday, y);
                const wSun = wMon.clone().add(6, 'days');
                totalPromises.push(
                    fetchFootfallTotal(wMon.format('YYYY-MM-DD'), wSun.format('YYYY-MM-DD'), zoneIds, [])
                );
            }
            const totalValues = await Promise.all(totalPromises);

            const tRows: IReportComparisonRow[] = totalValues.map((val, y) => {
                const wMon = getComparableMonday(monday, y);
                const wSun = wMon.clone().add(6, 'days');
                return {
                    year: wMon.year(),
                    label: wMon.format('DD.MM') + '–' + wSun.format('DD.MM.YYYY'),
                    dateFrom: wMon.format('YYYY-MM-DD'),
                    dateTo: wSun.format('YYYY-MM-DD'),
                    value: val
                };
            });
            setTotalRows(tRows);

            // --- Section 2: By Location ---
            const locRows: IReportBreakdownRow[] = [];
            for (const loc of mart.location) {
                const values: { year: number; value: number }[] = [];
                const locPromises: Promise<number>[] = [];
                for (let y = 0; y <= yearsBack; y++) {
                    const wMon = getComparableMonday(monday, y);
                    const wSun = wMon.clone().add(6, 'days');
                    locPromises.push(
                        fetchFootfallTotal(wMon.format('YYYY-MM-DD'), wSun.format('YYYY-MM-DD'), [], [loc.id])
                    );
                }
                const locValues = await Promise.all(locPromises);
                locValues.forEach((val, y) => {
                    values.push({ year: getComparableMonday(monday, y).year(), value: val });
                });
                locRows.push({ label: loc.name, values });
            }
            setLocationRows(locRows);

            // --- Section 3: By Zone ---
            const zRows: IReportBreakdownRow[] = [];
            for (const zone of mart.zone) {
                const values: { year: number; value: number }[] = [];
                const zPromises: Promise<number>[] = [];
                for (let y = 0; y <= yearsBack; y++) {
                    const wMon = getComparableMonday(monday, y);
                    const wSun = wMon.clone().add(6, 'days');
                    zPromises.push(
                        fetchFootfallTotal(wMon.format('YYYY-MM-DD'), wSun.format('YYYY-MM-DD'), [zone.id], [])
                    );
                }
                const zValues = await Promise.all(zPromises);
                zValues.forEach((val, y) => {
                    values.push({ year: getComparableMonday(monday, y).year(), value: val });
                });
                zRows.push({ label: zone.name, values });
            }
            setZoneRows(zRows);

            // --- Section 4: Daily Breakdown (no date field - just day names) ---
            const dayNames = [
                t`Pondelok`, t`Utorok`, t`Streda`, t`Štvrtok`,
                t`Piatok`, t`Sobota`, t`Nedeľa`
            ];
            const dRows: IReportBreakdownRow[] = [];
            for (let d = 0; d < 7; d++) {
                const values: { year: number; value: number }[] = [];
                const dayPromises: Promise<number>[] = [];
                for (let y = 0; y <= yearsBack; y++) {
                    const wMon = getComparableMonday(monday, y);
                    const dayDate = wMon.clone().add(d, 'days');
                    dayPromises.push(
                        fetchFootfallTotal(dayDate.format('YYYY-MM-DD'), dayDate.format('YYYY-MM-DD'), zoneIds, [])
                    );
                }
                const dayValues = await Promise.all(dayPromises);
                dayValues.forEach((val, y) => {
                    values.push({ year: getComparableMonday(monday, y).year(), value: val });
                });
                dRows.push({
                    label: dayNames[d],
                    values
                });
            }
            setDailyRows(dRows);
        } catch (e) {
            console.error('Error fetching weekly report data', e);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mart, mondayStr, yearsBack]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Convert totalRows to breakdown format for the table
    const totalBreakdownRows: IReportBreakdownRow[] = totalRows.length > 0
        ? [{ label: t`Celkom`, values: totalRows.map(r => ({ year: r.year, value: r.value })) }]
        : [];

    const buildXlsxInput = () => ({
        martName:   mart.name,
        reportType: t`Týždenný report`,
        dateLabel:  `${t`Týždeň`} ${weekNum} (${monday.format('DD.MM')}–${monday.clone().add(6, 'days').format('DD.MM.YYYY')})`,
        weekNum,
        columns:    yearColumns,
        sections: [
            ...(totalBreakdownRows.length > 0
                ? [{ title: t`Celková týždenná návštevnosť`, rows: totalBreakdownRows, layout: 'total' as const, chartId: 'weekly-total' }]
                : []),
            ...(locationRows.length > 0
                ? [{ title: t`Návštevnosť podľa vstupov`, rows: locationRows, layout: 'breakdown' as const, chartId: 'weekly-location' }]
                : []),
            ...(zoneRows.length > 0
                ? [{ title: t`Návštevnosť podľa zón`, rows: zoneRows, layout: 'breakdown' as const, chartId: 'weekly-zone' }]
                : []),
            ...(dailyRows.length > 0
                ? [{ title: t`Denný prehľad v týždni`, rows: dailyRows, layout: 'breakdown' as const, chartId: 'weekly-daily' }]
                : []),
        ],
        kpis: totalRows.length > 0 ? [
            {
                label: t`Celková návštevnosť`,
                value: numberWithSpaces(totalRows[0].value),
                sub:   `${t`Týždeň`} ${weekNum}`,
            },
        ] : undefined,
    });

    return (
        <>
            {/* Section 1: Total footfall comparison */}
            <div className="box mgt-30 mgb-30 pd-40 pdl-25lg pdr-25lg" style={{ position: 'relative' }}>
                <div className="loader-wrapper" style={{ position: loading ? 'relative' : undefined, overflow: loading ? 'hidden' : undefined, minHeight: loading ? 120 : undefined }}>
                    <Loader value={loading} />
                </div>
                {!loading && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                            <ExportButtons buildInput={buildXlsxInput} />
                        </div>
                        <ReportComparisonChart
                            chartId="weekly-total"
                            title={t`Celková týždenná návštevnosť`}
                            subtitle={t`Týždeň` + ' ' + weekNum + ' (' + monday.format('DD.MM.YYYY') + ' – ' + monday.clone().add(6, 'days').format('DD.MM.YYYY') + ')'}
                            rows={totalRows}
                        />
                        <ReportComparisonTable
                            title={t`Celková týždenná návštevnosť`}
                            columns={yearColumns}
                            rows={totalBreakdownRows}
                        />
                    </>
                )}
            </div>

            {/* Section 2: By Location */}
            {!loading && locationRows.length > 0 && (
                <div className="box mgt-30 mgb-30 pd-40 pdl-25lg pdr-25lg">
                    <ReportGroupedBarChart
                        chartId="weekly-location"
                        title={t`Porovnanie vstupov`}
                        columns={yearColumns}
                        rows={locationRows}
                    />
                    <ReportComparisonTable
                        title={t`Návštevnosť podľa vstupov`}
                        columns={yearColumns}
                        rows={locationRows}
                    />
                </div>
            )}

            {/* Section 3: By Zone */}
            {!loading && zoneRows.length > 0 && (
                <div className="box mgt-30 mgb-30 pd-40 pdl-25lg pdr-25lg">
                    <ReportGroupedBarChart
                        chartId="weekly-zone"
                        title={t`Porovnanie zón`}
                        columns={yearColumns}
                        rows={zoneRows}
                    />
                    <ReportComparisonTable
                        title={t`Návštevnosť podľa zón`}
                        columns={yearColumns}
                        rows={zoneRows}
                    />
                </div>
            )}

            {/* Section 4: Daily breakdown */}
            {!loading && dailyRows.length > 0 && (
                <div className="box mgt-30 mgb-30 pd-40 pdl-25lg pdr-25lg">
                    <ReportGroupedBarChart
                        chartId="weekly-daily"
                        title={t`Denný prehľad – graf`}
                        columns={yearColumns}
                        rows={dailyRows}
                    />
                    <ReportComparisonTable
                        title={t`Denný prehľad v týždni`}
                        columns={yearColumns}
                        rows={dailyRows}
                    />
                </div>
            )}
        </>
    );
};

export default ReportWeekly;
